import React from "react";
import { useSelector } from "react-redux";
import { Badge, Box, Grid, Typography, Tooltip, IconButton } from "@mui/material";
import { GppMaybe } from "@mui/icons-material";
import currency from "currency.js";
import moment from "moment";

import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CancelIcon from '@mui/icons-material/Cancel';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import UndoIcon from '@mui/icons-material/Undo';

import {
  ARL, CONTRACT_STATUS, PERMISSIONS, REVIEW_STATUS, REVIEW_STATUS_LABELS,
  REVIEW_DISPLAY_IN_CORRECTION
} from "../../constants";
import {
  resolveSocialSecurityPayment, getActivityStatus, getActivitiesAggregateStatus,
  getSocialSecurityStatus, getSocialSecurityDisplayStatus, getReviewDisplayStatus,
  getReviewIconColorProps, isMonthInCorrection, hasActivitiesReviewMark,
  isExecutablePeriod, getPreviousExecutableIdx, getPeriodNumber,
  getNextExecutableIdx, getLastApprovedPaymentIdx, getPeriodTaxValues
} from "../../functions";
import MoneyInput from "../generic/MoneyInput";
import { NEWS_TYPES } from "./ContractNewsForm";
import ContractViewActions from "./ContractViewActions";

const ContractViewPaymentPlan = ({
  contract,
  setContract,
  getContractFieldWithNews,
  getPeriodInfo,
  setShowChargeAccountModal,
  setShowSocialSecurity,
  setShowTemplate,
  setApprovedInfo,
  setRevertInfo,
  // Motivo de bloqueo por fecha de corte (payment-monthly-cutoff), o null.
  // Deshabilita la aprobación normal y la vacía; ADMIN llega con null.
  cutoffReason,
}) => {
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users);

  const supportIDs = contract.supportIDs || [];
  const supports = contract.supports || [];
  // Indica si el usuario es un enlace de Apoyo a la Supervisión asignado a este contrato.
  const isSupportReviewer =
    user.permissions.includes(PERMISSIONS.SUPPORT_SUPERVISION.REVIEW)
    && supportIDs.includes(user.userId);

  // Aprobación de cuentas de cobro vacías: la gobierna su propio permiso, no
  // el rol ni la supervisión del contrato. Por defecto solo lo lleva ADMIN,
  // pero puede concederse a cualquier usuario desde la administración.
  const canApproveEmpty = user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE_EMPTY);

  const supportName = (id) => {
    const support = supports.find((s) => s?.userId === id);
    return support ? `${support.name} ${support.lastName}` : id;
  };

  // Supervisor EFECTIVO (considerando novedades), no el campo estático supervisorIDs:
  // un supervisor asignado por novedad no queda en supervisorIDs pero sí es el supervisor vigente.
  const effectiveSupervisors = getContractFieldWithNews('supervisors') || [];
  const isContractSupervisor = effectiveSupervisors.some((s) => s?.userId === user.userId);

  const paymentPlan = getContractFieldWithNews('paymentPlan');

  // Reversa de aprobación: solo sobre la ÚLTIMA cuenta de cobro aprobada, con
  // su propio permiso (por defecto solo ADMIN). Para revertir varias se hace
  // en secuencia, de la más reciente hacia atrás.
  const canRevertApproval = user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.REVERT_APPROVAL);
  const lastApprovedIdx = canRevertApproval ? getLastApprovedPaymentIdx(paymentPlan, contract.payments) : -1;

  const downloadChargeAccount = (url) => {
    const separator = url.includes('?') ? '&' : '?';
    const a = document.createElement('a');
    a.href = `${url}${separator}v=${Date.now()}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <>
      {paymentPlan?.map((pay, idx) => {
        // Un mes sin valor no se ejecutó (suspendido por completo, o dentro del
        // hueco que deja una reanudación que empieza después del fin del
        // contrato): no genera cuenta de cobro. El índice se conserva porque
        // identifica el registro `payment_N`.
        if (!isExecutablePeriod(paymentPlan, idx)) return null;

        // El periodo anterior a efectos de encadenamiento y de seguridad
        // social es el último EJECUTABLE, no el índice inmediatamente
        // anterior: si no, un mes en cero dejaría bloqueadas todas las cuentas
        // siguientes exigiendo una SS que nadie debe pagar.
        const previousIdx = getPreviousExecutableIdx(paymentPlan, idx);
        // "Mes N" es el consecutivo de periodos ejecutables, no idx + 1: los
        // meses en cero no consumen número.
        const periodNumber = getPeriodNumber(paymentPlan, idx);
        const prevPeriodNumber = previousIdx >= 0 ? getPeriodNumber(paymentPlan, previousIdx) : null;

        const { salarioMinimo, uvt } = getPeriodTaxValues(contract.startedAt, idx);
        const salary = (pay * 0.4) > salarioMinimo ? (pay * 0.4) : salarioMinimo;
        const parent_approved = previousIdx < 0 ? true : contract.payments && contract.payments[`payment_${previousIdx}`]?.approved;
        const payment = contract.payments && contract.payments[`payment_${idx}`];
        const returnedActivitiesCount = payment?.activities?.filter(
          (act) => getActivityStatus(act) === REVIEW_STATUS.RETURNED
        ).length || 0;
        const hasReturnedActivities = returnedActivitiesCount > 0;

        // La cuenta de cobro del Mes N exige la SS del Mes N-1 revisada (pago
        // a mes vencido): el icono de SS de la fila referencia por eso al
        // periodo ANTERIOR. El mes 0 no tiene periodo previo: se validan los
        // certificados de afiliación del contrato.
        const prevPayment = previousIdx >= 0 ? contract.payments?.[`payment_${previousIdx}`] : null;
        const ssPrevStatus = getSocialSecurityStatus(prevPayment);
        const ssOwnStatus = getSocialSecurityStatus(payment);
        // Pago anticipado (payAt de la SS del mes dentro del mes de la cuenta,
        // el "SI" de la plantilla): la SS del PROPIO mes también debe estar
        // revisada; la fila lo señala con un badge clickeable.
        const ssAnticipado = resolveSocialSecurityPayment(contract, idx, getContractFieldWithNews).isSameMonth;
        const socialReviewed = (previousIdx < 0 || ssPrevStatus === REVIEW_STATUS.REVIEWED)
          && (!ssAnticipado || ssOwnStatus === REVIEW_STATUS.REVIEWED);
        // Alguna SS exigida devuelta: pendiente de corrección del contratista.
        const ssReturned = (previousIdx >= 0 && ssPrevStatus === REVIEW_STATUS.RETURNED)
          || (ssAnticipado && ssOwnStatus === REVIEW_STATUS.RETURNED);

        // Estado agregado de las actividades del mes (peor estado) para la
        // zona informativa.
        const activitiesStatus = getActivitiesAggregateStatus(payment, supportIDs);
        // Ciclo de corrección: Rojo desde la notificación de devoluciones hasta
        // la notificación de corrección del contratista.
        const inCorrection = isMonthInCorrection(payment);
        // El mes pasa a Amarillo solo con el ENVÍO a revisión: registrar
        // actividades sin enviar lo mantiene Gris. Las devoluciones sin
        // notificar tampoco cambian el color (siguen "en revisión"): el
        // contratista no debe percibir nada antes de la notificación.
        const monthSent = payment?.approved !== undefined;
        const activitiesStatusForMonth = activitiesStatus === REVIEW_STATUS.RETURNED
          ? REVIEW_STATUS.PENDING
          : activitiesStatus;
        const activitiesDisplayStatus = inCorrection
          ? REVIEW_DISPLAY_IN_CORRECTION
          : getReviewDisplayStatus(activitiesStatusForMonth, monthSent);
        // La SS es independiente del envío del mes: su color depende solo del
        // diligenciamiento y la revisión del propio componente.
        const ssPrevDisplayStatus = getSocialSecurityDisplayStatus(prevPayment);
        const ssOwnDisplayStatus = getSocialSecurityDisplayStatus(payment);
        // Badge rojo (x): devoluciones vigentes sin notificar. Solo lo ve la
        // revisión (Supervisor/Apoyo/ADMIN); nunca el contratista.
        const canSeeReturnBadge = user.roles.includes('ADMIN')
          || isContractSupervisor
          || isSupportReviewer;
        const showReturnBadge = canSeeReturnBadge && hasReturnedActivities && !inCorrection;

        // Prerrequisitos de la aprobación del acta: actividades marcadas como
        // revisadas (Apoyo o Supervisor) y, para el mes 0, los certificados de
        // afiliación del contrato (la SS del mes 0 se valida a mes vencido).
        const activitiesReviewed = hasActivitiesReviewMark(payment);
        const hasAffiliationCerts = !!(
          contract.certificates?.eps && contract.certificates?.afp && contract.certificates?.arl
        );
        // Periodo vacío: el contratista no registró actividades. Habilita la
        // aprobación vacía del ADMIN (ver el atajo más abajo).
        const isEmptyPeriod = !payment?.activities?.length;
        // Periodo ya cerrado por aprobación vacía: no tiene cuenta de cobro ni
        // seguridad social que revisar, así que la fila oculta el
        // previsualizador, el icono de SS y el menú de documentos del periodo.
        const approvedEmpty = !!payment?.approvedEmpty;
        // Atajo de aprobación vacía disponible en esta fila: periodo sin
        // actividades, todavía sin aprobar y con el permiso concedido.
        const showEmptyApproval = canApproveEmpty && isEmptyPeriod && !payment?.approved;

        // Marcas de revisión (Supervisor o Apoyo) y devoluciones del periodo
        // de SS referenciado por la fila (mes anterior), para el tooltip.
        const ssPrevReviews = Object.entries(prevPayment?.socialSecurityReview?.reviews || {});
        const ssPrevReturns = (prevPayment?.socialSecurityReview?.returns || []).filter((ret) => !ret.undone);
        const canSeeReviewers = user.roles.includes('ADMIN')
          || user.roles.includes('ADMIN_CONTRACT')
          || isContractSupervisor
          || isSupportReviewer;

        // Fechas reales del periodo (días ejecutables, no el mes calendario) y
        // las novedades que lo tocan, que alimentan el aviso de la fila.
        const newsArr = [];
        const periodInfo = getPeriodInfo(idx, newsArr);
        const rangeDate = periodInfo?.range;
        // Días suspendidos DENTRO del rango: una suspensión que parte el mes en
        // dos tramos no cabe en la etiqueta, así que se detalla en el aviso.
        const suspendedRanges = (periodInfo?.gaps || [])
          .map(({ from, to }) => (from === to ? `${from}` : `${from} a ${to}`))
          .join(', ');

         return (
          <React.Fragment key={idx}>
            <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ whiteSpace: "nowrap", mr: 2 }}>Mes {periodNumber}: </Typography>
                <Typography sx={{ color: "gray", fontSize: "14px" }}>({rangeDate})</Typography>
              </Box>
              {newsArr.length > 0
                && <Tooltip
                  title={(
                    <Box>
                      {newsArr.map((news, nidx) => (
                        <Box key={nidx}>
                          <Typography>{NEWS_TYPES[news.type]}</Typography>
                        </Box>
                      ))}
                      {suspendedRanges && (
                        <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                          {`Días suspendidos del periodo: ${suspendedRanges}`}
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  <GppMaybe sx={{ color: "gray", fontSize: 20, marginRight: 1 }}/>
                </Tooltip>
              }
            </Grid>
            <Grid item xs={3}>
              <MoneyInput
                label="Valor a pagar"
                value={pay}
                readOnly
              />
            </Grid>
            <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
              <MoneyInput
                label="Pago Seguridad Social"
                value={salary * (0.12 + 0.165 + parseFloat(ARL[contract.arlLevel].value))}
                disabled
              />
              <Tooltip
                title={(
                  <Grid container sx={{ p: 1, fontSize: 16 }}>
                    <Grid item xs={6}>Salud</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(salary * 0.125, { precision: 0 }).format()}
                    </Grid>
                    <Grid item xs={6}>Pensión</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(salary * 0.16, { precision: 0 }).format()}
                    </Grid>
                    <Grid item xs={6}>ARL</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(salary * parseFloat(ARL[contract.arlLevel].value), { precision: 0 }).format()}
                    </Grid>
                  </Grid>
                )}
              >
                <IconButton sx={{ marginLeft: -4 }}>
                  <InfoIcon sx={{ color: "gray", fontSize: 20 }}/>
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
              <MoneyInput
                label="Estampillas"
                value={pay * (0.004 + (pay > 145 * uvt ? 0.01 : 0) + 0.02 + 0.006 + 0.004 + (pay > 300 * uvt ? 0.004 : 0))}
                disabled
              />
              <Tooltip
                title={(
                  <Grid container sx={{ p: 1, fontSize: 16 }}>
                    <Grid item xs={6}>D59 (IU Env)</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(pay * 0.004, { precision: 0 }).format()}
                    </Grid>
                    <Grid item xs={6}>D63 (Pro Hosp)</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {pay > 145 * uvt ? currency(pay * 0.01, { precision: 0 }).format() : 0}
                    </Grid>
                    <Grid item xs={6}>D87 (Pro Bien)</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(pay * 0.02, { precision: 0 }).format()}
                    </Grid>
                    <Grid item xs={6}>D88 (Pro Dllo)</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(pay * 0.006, { precision: 0 }).format()}
                    </Grid>
                    <Grid item xs={6}>D89 (Politécnico)</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {currency(pay * 0.004, { precision: 0 }).format()}
                    </Grid>
                    <Grid item xs={6}>D124 (IU Digital)</Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      {pay > 300 * uvt ? currency(pay * 0.004, { precision: 0 }).format() : 0}
                    </Grid>
                  </Grid>
                )}
              >
                <IconButton sx={{ marginLeft: -4 }}>
                  <InfoIcon sx={{ color: "gray", fontSize: 20 }}/>
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={2}>
              {parent_approved
                && (getContractFieldWithNews('status') !== CONTRACT_STATUS.CLOSED || user.roles.includes('ADMIN'))
                && <>
                  {!approvedEmpty && (
                    <Tooltip title="Previsualizar Cuenta de cobro">
                      <IconButton size="small" onClick={() => setShowTemplate(`payment_${idx}`)}>
                        <DescriptionIcon color={payment?.approved ? "success": "action"} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Periodo cerrado por aprobación vacía: no hay actividades,
                      cuenta de cobro ni seguridad social que consultar. Un único
                      icono informativo (gris: cierre administrativo, ni logro ni
                      hallazgo) sustituye toda la fila de acciones. */}
                  {approvedEmpty && (
                    <Tooltip title={(
                      <Box sx={{ p: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                          Cuenta de cobro vacía — aprobada sin actividades
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                          El periodo se cerró sin actividades registradas y no genera cuenta de cobro.
                        </Typography>
                        {canSeeReturnBadge && (
                          <Typography sx={{ fontSize: 12, mt: 0.75 }}>
                            Aprobada por {payment.approvedEmptyBy?.name || 'un administrador'}
                            {payment.approvedEmptyBy?.at
                              ? ` — ${moment(payment.approvedEmptyBy.at).format('YYYY-MM-DD')}`
                              : ''}
                          </Typography>
                        )}
                      </Box>
                    )}>
                      <Box component="span" sx={{ display: 'inline-flex', p: '5px' }}>
                        <EventBusyIcon color="action" />
                      </Box>
                    </Tooltip>
                  )}
                  {!approvedEmpty && (<>
                    {/* Actividades: abre el modal; el tooltip informa el estado de
                        revisión del componente (solo actividades, sin SS). */}
                    <Tooltip title={(
                      <Box sx={{ p: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                          Actividades — {REVIEW_STATUS_LABELS[activitiesDisplayStatus]}
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                          {payment?.activities?.length > 0
                            ? `${payment.activities.length} actividad(es) registrada(s)`
                            : 'Sin actividades registradas'}
                        </Typography>
                        {/* Devoluciones: antes de notificar solo las ve la revisión;
                            con el mes en corrección las ve también el contratista. */}
                        {returnedActivitiesCount > 0 && (inCorrection || canSeeReturnBadge) && (
                          <Typography sx={{ fontSize: 12 }}>
                            {inCorrection
                              ? `${returnedActivitiesCount} devuelta(s), en corrección por el contratista`
                              : `${returnedActivitiesCount} devuelta(s) sin notificar`}
                          </Typography>
                        )}
                        {/* Marcas de revisión (Apoyo o Supervisor): solo si al menos
                            una existe. */}
                        {canSeeReviewers && Object.keys(payment?.activitiesReview?.reviews || {}).length > 0 && (
                          <>
                            <Typography sx={{ fontWeight: 600, fontSize: 12, mt: 0.75 }}>
                              Revisión de actividades
                            </Typography>
                            {Object.entries(payment?.activitiesReview?.reviews || {}).map(([id, mark]) => (
                              <Typography key={id} sx={{ fontSize: 12 }}>
                                ✓ {mark?.name || supportName(id)}
                                {mark?.at
                                  ? ` — ${moment(mark.at).format('YYYY-MM-DD')}`
                                  : ''}
                              </Typography>
                            ))}
                          </>
                        )}
                        {payment?.observations?.length > 0 && (
                          <>
                            <Typography sx={{ fontWeight: 600, fontSize: 12, mt: 0.75 }}>
                              Observaciones ({payment.observations.length})
                            </Typography>
                            {payment.observations.map((obs, oidx) => (
                              <Box key={oidx} sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{obs.name}</Typography>
                                <Typography sx={{ fontSize: 12 }}>{obs.text}</Typography>
                              </Box>
                            ))}
                          </>
                        )}
                      </Box>
                    )}>
                      <IconButton size="small" onClick={() => setShowChargeAccountModal(`payment_${idx}`)}>
                        <Badge
                          overlap="circular"
                          invisible={!showReturnBadge}
                          badgeContent={<CancelIcon sx={{ fontSize: 14 }} color="error" />}
                        >
                          <ListAltIcon {...getReviewIconColorProps(activitiesDisplayStatus)} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  </>)}
                  {/* La aprobación vacía cierra el periodo sin exigir seguridad
                      social: el icono no aplica y se oculta. */}
                  {!approvedEmpty && (<>
                    {/* Seguridad social: la fila del Mes N referencia al Mes N-1,
                        el periodo exigido para aprobar esta cuenta. El Mes 1 no
                        tiene periodo previo: su icono informa los certificados de
                        afiliación del contrato. Con pago anticipado, un badge
                        clickeable señala la SS del propio mes, también exigida. */}
                    <Badge
                      overlap="circular"
                      invisible={!ssAnticipado}
                      badgeContent={(() => {
                        const badgeColorProps = getReviewIconColorProps(ssOwnDisplayStatus);
                        return (
                          <Tooltip title={`Seguridad social del Mes ${periodNumber} (pago anticipado) — ${REVIEW_STATUS_LABELS[ssOwnDisplayStatus]}. También debe estar revisada para aprobar esta cuenta de cobro.`}>
                            <HealthAndSafetyIcon
                              {...badgeColorProps}
                              onClick={(evt) => {
                                evt.stopPropagation();
                                setShowSocialSecurity(`payment_${idx}`);
                              }}
                              sx={{ ...(badgeColorProps.sx || {}), fontSize: 15, cursor: 'pointer' }}
                            />
                          </Tooltip>
                        );
                      })()}
                    >
                      {previousIdx < 0
                        ? (
                          <Tooltip title={(
                            <Box sx={{ p: 0.5 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                                Certificados de afiliación del contrato
                              </Typography>
                              <Typography sx={{ fontSize: 12 }}>
                                El Mes 1 no exige seguridad social previa: su aprobación requiere los certificados de afiliación.
                              </Typography>
                              {['eps', 'afp', 'arl'].map((key) => (
                                <Typography key={key} sx={{ fontSize: 12 }}>
                                  {contract.certificates?.[key] ? '✓' : '✗'} {key.toUpperCase()} — {contract.certificates?.[key] ? 'cargado' : 'faltante'}
                                </Typography>
                              ))}
                            </Box>
                          )}>
                            <Box component="span" sx={{ display: 'inline-flex', p: '5px' }}>
                              <HealthAndSafetyIcon color={hasAffiliationCerts ? 'success' : 'action'} />
                            </Box>
                          </Tooltip>
                        )
                        : (
                          <Tooltip title={(
                            <Box sx={{ p: 0.5 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                                Seguridad social del Mes {prevPeriodNumber} — {REVIEW_STATUS_LABELS[ssPrevDisplayStatus]}
                              </Typography>
                              <Typography sx={{ fontSize: 12 }}>
                                Periodo exigido para aprobar la cuenta del Mes {periodNumber}
                              </Typography>
                              {canSeeReviewers && ssPrevReviews.length > 0 && (
                                <>
                                  <Typography sx={{ fontWeight: 600, fontSize: 12, mt: 0.75 }}>
                                    Revisión
                                  </Typography>
                                  {ssPrevReviews.map(([id, mark]) => (
                                    <Typography key={id} sx={{ fontSize: 12 }}>
                                      ✓ {mark?.name || supportName(id)}
                                      {mark?.at ? ` — ${moment(mark.at).format('YYYY-MM-DD')}` : ''}
                                    </Typography>
                                  ))}
                                </>
                              )}
                              {ssPrevReturns.length > 0 && (
                                <>
                                  <Typography sx={{ fontWeight: 600, fontSize: 12, mt: 0.75 }}>
                                    Devoluciones ({ssPrevReturns.length})
                                  </Typography>
                                  {ssPrevReturns.map((ret, ridx) => (
                                    <Box key={ridx} sx={{ mb: 0.5 }}>
                                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                        {ret.name} — {moment(ret.at).format('YYYY-MM-DD')}
                                      </Typography>
                                      <Typography sx={{ fontSize: 12 }}>{ret.note}</Typography>
                                    </Box>
                                  ))}
                                </>
                              )}
                            </Box>
                          )}>
                            <IconButton size="small" onClick={() => setShowSocialSecurity(`payment_${previousIdx}`)}>
                              <HealthAndSafetyIcon {...getReviewIconColorProps(ssPrevDisplayStatus)} />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Badge>
                  </>)}

                  {/* Menú de documentos del periodo (cuenta de cobro, consolidado,
                      zip): sin cuenta de cobro generada no tiene nada que ofrecer
                      y permitiría generar el PDF que la aprobación vacía evita. */}
                  {payment?.approved && !approvedEmpty
                    && <ContractViewActions
                      contract={contract}
                      setContract={setContract}
                      payment={payment}
                      paymentKey={`payment_${idx}`}
                      idx={idx}
                      user={user}
                      users={users}
                      getContractFieldWithNews={getContractFieldWithNews}
                      downloadChargeAccount={downloadChargeAccount}
                    />
                  }
                  {idx === lastApprovedIdx && setRevertInfo && (() => {
                    // El periodo siguiente depende de esta aprobación
                    // (encadenamiento): si ya tiene trabajo, se advierte que
                    // queda bloqueado hasta que esta se apruebe de nuevo.
                    const nextIdx = getNextExecutableIdx(paymentPlan, idx);
                    const nextPayment = nextIdx >= 0 ? contract.payments?.[`payment_${nextIdx}`] : null;
                    const nextStarted = nextPayment?.approved !== undefined || nextPayment?.activities?.length > 0;
                    return (
                      <Tooltip title="Devolver cuenta de cobro aprobada">
                        <IconButton size="small" onClick={() => setRevertInfo({
                          open: true,
                          title: (
                            <Box>
                              ¿Desea devolver esta cuenta de cobro aprobada?
                              <Box sx={{ fontSize: ".85em", display: "block", marginTop: 1, textAlign: "center" }}>
                                {`Mes ${periodNumber} (${rangeDate})`}
                              </Box>
                              <Box sx={{ fontSize: ".65em", fontWeight: "normal", color: "#e17f7f", textAlign: "center", marginTop: 1 }}>
                                {approvedEmpty
                                  ? 'Se anulará la aprobación sin actividades y el periodo volverá a quedar pendiente.'
                                  : 'Se anulará la aprobación y la cuenta de cobro generada. El acta volverá a revisión del Supervisor, que podrá devolver actividades al contratista o aprobarla de nuevo.'}
                              </Box>
                            </Box>
                          ),
                          warning: nextStarted
                            ? `El Mes ${getPeriodNumber(paymentPlan, nextIdx)} ya tiene actividades registradas o enviadas: quedará bloqueado hasta que este periodo se apruebe de nuevo.`
                            : undefined,
                          confirm: 'Devolver',
                          data: { payment: `payment_${idx}` },
                        })}>
                          <UndoIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    );
                  })()}
                  {/* Aprobación de cuenta de cobro VACÍA. Existe para los contratos
                      montados tarde, cuyos primeros periodos ya pasaron sin
                      actividad y deben quedar aprobados para poder trabajar el mes
                      corriente. La habilita el permiso APPROVE_EMPTY por sí solo
                      — sin exigir el rol ADMIN ni la supervisión del contrato — y
                      salta TODOS los prerrequisitos (actividades revisadas,
                      seguridad social y certificados de afiliación), que ninguno
                      de esos periodos puede cumplir. Tiene prioridad sobre el
                      flujo normal de aprobación. */}
                  {showEmptyApproval && cutoffReason && (
                    <Tooltip title={cutoffReason}>
                      <span>
                        <IconButton size="small" disabled>
                          <TaskAltIcon color="disabled" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {showEmptyApproval && !cutoffReason && (
                    <Tooltip title="Aprobar cuenta de cobro sin actividades">
                      <IconButton size="small" onClick={() => setApprovedInfo({
                        open: true,
                        title: (
                          <Box>
                            ¿Desea aprobar esta cuenta de cobro sin actividades?
                            <Box sx={{ fontSize: ".85em", display: "block", marginTop: 1, textAlign: "center" }}>
                              {`Mes ${periodNumber} (${rangeDate})`}
                            </Box>
                            <Box sx={{ fontSize: ".65em", fontWeight: "normal", color: "#e17f7f", textAlign: "center", marginTop: 1 }}>
                              El periodo no tiene actividades registradas y quedará aprobado
                              sin revisión de actividades ni de seguridad social.
                              No se notificará al contratista.
                            </Box>
                          </Box>
                        ),
                        data: { payment: `payment_${idx}`, emptyApproval: true },
                      })}>
                        <TaskAltIcon color="warning" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!showEmptyApproval
                    && user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE)
                    && (user.roles.includes('ADMIN') || isContractSupervisor)
                    && (user.roles.includes('ADMIN') ? !payment?.approved : payment?.approved === false)
                    && (() => {
                        // Fecha de corte: pasado el corte del mes no se aprueba
                        // hasta el día 1. Va antes que los demás motivos porque
                        // ninguno de ellos se puede resolver durante la ventana.
                        if (cutoffReason) {
                          return (
                            <Tooltip title={cutoffReason}>
                              <span>
                                <IconButton size="small" disabled>
                                  <TaskAltIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // Mes en corrección: la pelota está en la cancha del
                        // contratista; la revisión/aprobación se reanuda con su
                        // notificación de corrección.
                        if (inCorrection) {
                          return (
                            <Tooltip title="Mes en corrección por el contratista — pendiente de su notificación de corrección">
                              <span>
                                <IconButton size="small" disabled>
                                  <HourglassEmptyIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // La devolución al contratista ocurre por componente
                        // (actividad o seguridad social), no por acta completa.
                        // Con actividades devueltas el acta no se aprueba.
                        if (hasReturnedActivities) {
                          return (
                            <Tooltip title="Actividades devueltas sin notificar — envíe la notificación de devoluciones o deshágalas">
                              <span>
                                <IconButton size="small" disabled>
                                  <HourglassEmptyIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // No se puede aprobar el acta con alguna seguridad
                        // social exigida devuelta: pendiente del contratista.
                        if (ssReturned) {
                          const returnedMonths = [
                            ...(previousIdx >= 0 && ssPrevStatus === REVIEW_STATUS.RETURNED ? [`Mes ${prevPeriodNumber}`] : []),
                            ...(ssAnticipado && ssOwnStatus === REVIEW_STATUS.RETURNED ? [`Mes ${periodNumber}`] : []),
                          ].join(' y ');
                          return (
                            <Tooltip title={`Seguridad social del ${returnedMonths} devuelta — pendiente de corrección del contratista`}>
                              <span>
                                <IconButton size="small" disabled>
                                  <HourglassEmptyIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // Prerrequisito de revisión: las actividades deben estar
                        // marcadas como revisadas (por el Apoyo o el Supervisor)
                        // antes de poder aprobar la cuenta de cobro.
                        if (!activitiesReviewed) {
                          return (
                            <Tooltip title="Primero deben marcarse las actividades como revisadas">
                              <span>
                                <IconButton size="small" disabled>
                                  <TaskAltIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // Gating del mes 0: la SS se valida a mes vencido (no hay
                        // mes anterior en el contrato), así que se exigen los
                        // certificados de afiliación del contrato en su lugar.
                        if (previousIdx < 0 && !hasAffiliationCerts) {
                          return (
                            <Tooltip title="Faltan certificados de afiliación del contrato (EPS, AFP y ARL)">
                              <span>
                                <IconButton size="small" disabled>
                                  <TaskAltIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // Gating Seguridad Social: el botón de aprobar se
                        // muestra deshabilitado hasta que la SS del periodo
                        // anterior (y la del mes con pago anticipado) esté
                        // Revisada, como guía del paso pendiente.
                        if (!socialReviewed) {
                          const pendingMonths = [
                            ...(previousIdx >= 0 && ssPrevStatus !== REVIEW_STATUS.REVIEWED ? [`Mes ${prevPeriodNumber}`] : []),
                            ...(ssAnticipado && ssOwnStatus !== REVIEW_STATUS.REVIEWED ? [`Mes ${periodNumber} (pago anticipado)`] : []),
                          ].join(' y ');
                          return (
                            <Tooltip title={`Primero debe marcarse revisada la seguridad social del ${pendingMonths}`}>
                              <span>
                                <IconButton size="small" disabled>
                                  <TaskAltIcon color="disabled" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        return (
                          <Tooltip title="Aprobar cuenta de cobro">
                            <IconButton size="small" onClick={() => setApprovedInfo({
                              open: true,
                              title: (
                                <Box>
                                  ¿Desea aprobar esta cuenta de cobro?
                                  <Box sx={{ fontSize: ".85em", display: "block", marginTop: 1, textAlign: "center" }}>
                                    {`Mes ${periodNumber} (${rangeDate})`}
                                  </Box>
                                  {!contract.payments?.[`payment_${idx}`]?.activities?.length
                                    && <Box sx={{ fontSize: ".65em", fontWeight: "normal", color: "#e17f7f", textAlign: "center" }}>
                                      No hay actividades registradas
                                    </Box>
                                  }
                                </Box>
                              ),
                              data: { payment: `payment_${idx}` },
                            })}>
                              <TaskAltIcon color="success" />
                            </IconButton>
                          </Tooltip>
                        );
                      })()
                  }
                </>
              }
            </Grid>
          </React.Fragment>
         );
      })}
    </>
  );
};

export default ContractViewPaymentPlan;
